import {
  EnumOrderApprovalStatus,
  EnumProductType,
  Multilingual,
  StorefrontCalendarFeedQuery,
  StorefrontCalendarKind,
  useStorefrontCalendarFeedQuery,
} from "gql/generated";
import _ from "lodash";
import { useMemo, useState } from "react";
import { YearGroup } from "./components";

export enum CalendarItemType {
  Order = "ORDER",
  Closure = "CLOSURE",
  EventSale = "EVENT_SALE",
  Today = "TODAY",
}

export type EventOrderArrivalEntry = {
  __typename?: "EventOrderArrivalEntry";
  action: string;
  source: string;
  sessionIds?: Array<string> | null;
  guestIndexes: Array<number>;
  attendanceKeys?: Array<string> | null;
  note?: string | null;
  timestamp: string;
  performedBy?: string | null;
  performedByName?: string | null;
};

export type EventSessionGuest = {
  name: string;
  email?: string | null;
  eventOptions?: Array<{
    label: string;
    sourceTransactionId?: string | null;
    amount?: number | null;
  } | null> | null;
};

export type EventSessionOrder = {
  orderId: string;
  purchaseDate: string;
  customerName: string;
  tierName: string;
  quantity: number;
  cancelled: boolean;
  guestNames?: Array<string | null> | null;
  guests?: Array<EventSessionGuest | null> | null;
  sessionIds?: Array<string> | null;
  isPass?: boolean;
  arrivalHistory?: Array<EventOrderArrivalEntry | null> | null;
};

type CalendarFeed = NonNullable<
  StorefrontCalendarFeedQuery["storefrontCalendarFeed"]
>;
export type CalendarProduct = NonNullable<
  NonNullable<CalendarFeed["products"]>[number]
>;
export type CalendarClosure = NonNullable<
  NonNullable<
    NonNullable<CalendarProduct["accommodationMetadata"]>["closures"]
  >[number]
>;

export type CalendarItem = {
  _id: string;
  startDate: string;
  endDate: string;
  type: CalendarItemType;
  cancelled?: boolean;
  /** Booking request still waiting for the business to approve it. */
  awaitingApproval?: boolean;
  closureName?: string;
  closureProducts?: { _id: string; name: Multilingual }[];
  customerName?: string;
  productName?: Multilingual;
  guests?: number;
  eventId?: string;
  eventName?: string;
  ticketTypeName?: string;
  totalSold?: number;
  passesSold?: number;
  ticketsSold?: number;
  arrivedCount?: number;
  soldTimeSlots?: string[];
  sessionId?: string;
  timeSlotLabel?: string | null;
  totalCapacity?: number | null;
  remainingCapacity?: number | null;
  isUnlimited?: boolean;
  eventYearSessionCount?: number;
};

const monthRange = (year: number, month: number) => ({
  start: new Date(Date.UTC(year, month, 1)).toISOString(),
  end: new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999)).toISOString(),
});

const overlaps = (
  startDate: string,
  endDate: string,
  rangeStart: string,
  rangeEnd: string,
) => startDate <= rangeEnd && endDate >= rangeStart;

export const useCalendarView = (
  storefrontId: string,
  productType: EnumProductType,
) => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const range = useMemo(
    () => monthRange(currentYear, currentMonth),
    [currentMonth, currentYear],
  );

  const { data, loading, error } = useStorefrontCalendarFeedQuery({
    variables: {
      storefrontId,
      kind:
        productType === EnumProductType.Event
          ? StorefrontCalendarKind.Event
          : StorefrontCalendarKind.Accommodation,
      rangeStart: range.start,
      rangeEnd: range.end,
      summaryYear: currentYear,
    },
    skip: !storefrontId,
  });

  const feed = data?.storefrontCalendarFeed;
  const today = useMemo(() => new Date(), []);
  const storefrontProducts = useMemo(
    () => _.compact(feed?.products ?? []),
    [feed?.products],
  );
  const allProducts = useMemo(
    () => storefrontProducts.filter(product => product.type === productType),
    [productType, storefrontProducts],
  );
  const availableProductTypes = useMemo(
    () =>
      _.uniq(
        storefrontProducts
          .map(product => product.type)
          .filter(type =>
            [EnumProductType.Accommodation, EnumProductType.Event].includes(
              type,
            ),
          ),
      ),
    [storefrontProducts],
  );

  const formattedOrderData: CalendarItem[] = useMemo(
    () =>
      _.compact(feed?.accommodationRows ?? []).map(row => ({
        _id: row.orderId,
        cancelled: row.cancelled,
        awaitingApproval:
          !row.cancelled && row.approvalStatus === EnumOrderApprovalStatus.Pending,
        customerName: row.customerName,
        productName: row.productName ?? undefined,
        startDate: row.startDate,
        endDate: row.endDate,
        guests: row.guests,
        type: CalendarItemType.Order,
      })),
    [feed?.accommodationRows],
  );

  const formattedEventSales: CalendarItem[] = useMemo(() => {
    const raw = _.compact(feed?.eventSales ?? []);
    const grouped = _.groupBy(
      raw,
      item => `${item.eventId}:${item.sessionDate}`,
    );
    return Object.entries(grouped).map(([key, items]) => {
      const first = items[0];
      const anyUnlimited = items.some(item => item.isUnlimited);
      const labels = _.uniq(_.compact(items.map(item => item.timeSlotLabel)));
      return {
        _id: key,
        startDate: first.sessionDate,
        endDate: first.sessionDate,
        type: CalendarItemType.EventSale,
        eventId: first.eventId,
        eventName: first.eventName,
        sessionId: first.sessionId,
        timeSlotLabel: labels.length ? labels.join(", ") : null,
        totalSold: first.eventDateTotalSold,
        passesSold: first.eventDatePassesSold,
        ticketsSold: first.eventDateTicketsSold,
        arrivedCount: first.eventDateArrivedCount,
        totalCapacity: anyUnlimited
          ? null
          : items.reduce((sum, item) => sum + (item.totalCapacity ?? 0), 0),
        remainingCapacity: anyUnlimited
          ? null
          : items.reduce((sum, item) => sum + (item.remainingCapacity ?? 0), 0),
        isUnlimited: anyUnlimited,
        eventYearSessionCount: first.eventYearSessionCount ?? undefined,
      };
    });
  }, [feed?.eventSales]);

  const formattedClosureData: CalendarItem[] = useMemo(() => {
    if (productType !== EnumProductType.Accommodation) return [];
    const withProducts = allProducts.flatMap(product =>
      _.compact(product.accommodationMetadata?.closures ?? []).map(closure => ({
        closure,
        product: { _id: product._id, name: product.name },
      })),
    );
    return Object.values(_.groupBy(withProducts, item => item.closure._id)).map(
      matches => ({
        _id: matches[0].closure._id,
        closureName: matches[0].closure.name,
        startDate: matches[0].closure.dateRangeISO.start,
        endDate: matches[0].closure.dateRangeISO.end,
        type: CalendarItemType.Closure,
        closureProducts: _.uniqBy(
          matches.map(match => match.product),
          "_id",
        ),
      }),
    );
  }, [allProducts, productType]);

  const closureDataByMonth = useMemo(
    () =>
      formattedClosureData.filter(closure =>
        overlaps(closure.startDate, closure.endDate, range.start, range.end),
      ),
    [formattedClosureData, range.end, range.start],
  );
  const monthSourceItems =
    productType === EnumProductType.Event
      ? formattedEventSales
      : formattedOrderData;
  const allCalendarItems = useMemo(
    () => [...monthSourceItems, ...closureDataByMonth],
    [closureDataByMonth, monthSourceItems],
  );

  const calendarItemsByCurrentMonth = useMemo(
    () =>
      _.orderBy(
        _.compact([
          ...allCalendarItems,
          today.getMonth() === currentMonth &&
          today.getFullYear() === currentYear &&
          allCalendarItems.length > 0
            ? {
                _id: "TODAY",
                startDate: today.toISOString(),
                endDate: today.toISOString(),
                type: CalendarItemType.Today,
              }
            : undefined,
        ]),
        ["startDate"],
        ["asc"],
      ),
    [allCalendarItems, currentMonth, currentYear, today],
  );

  const selectedMonths = _.compact(feed?.yearSummary?.months ?? []);
  const emptyMonths = Array.from({ length: 12 }, (_, month) => ({
    month,
    count: 0,
    hasBookings: false,
    hasClosures: false,
  }));
  const years = _.uniq([currentYear, ...(feed?.availableYears ?? [])]);
  const calendarItemsYearGroups: YearGroup[] = years.map(year => ({
    year,
    monthsAndCounts:
      year === currentYear && selectedMonths.length === 12
        ? selectedMonths
        : emptyMonths,
  }));
  const hasAnyCalendarItems = selectedMonths.some(month => month.count > 0);
  const nextAvailableCalendarDate = selectedMonths.find(
    month => month.month > currentMonth && month.count > 0,
  )
    ? {
        year: currentYear,
        month: selectedMonths.find(
          month => month.month > currentMonth && month.count > 0,
        )!.month,
      }
    : undefined;

  const [hoveredDateRangeISO, setHoveredDateRangeISO] = useState<
    | {
        start: string;
        end: string;
        isOpen?: boolean;
      }
    | undefined
  >();
  const updateHoveredDateRangeISO = (
    start?: string,
    end?: string,
    isOpen?: boolean,
  ) => {
    setHoveredDateRangeISO(start && end ? { start, end, isOpen } : undefined);
  };

  const allClosures = useMemo(
    () =>
      _.uniqBy(
        _.compact(
          allProducts.flatMap(
            product => product.accommodationMetadata?.closures ?? [],
          ),
        ),
        "_id",
      ),
    [allProducts],
  );

  return {
    loading,
    error,
    allCalendarItems,
    calendarItemsByCurrentMonth,
    currentMonth,
    onMonthChange: setCurrentMonth,
    currentYear,
    onYearChange: setCurrentYear,
    calendarItemsYearGroups,
    hasAnyCalendarItems,
    nextAvailableCalendarDate,
    hoveredDateRangeISO,
    updateHoveredDateRangeISO,
    allClosures,
    allProducts,
    availableProductTypes,
  };
};
